import type { UITable } from '../../components/index';
import type { UIDynamicTableRecord } from '../../components/table/ui-dynamic-table.types';
import type { ComponentPlugin } from '../../components/ui-element.type';
import { NullOr } from '../../global/utils.type';
import type {
    UISelectablePluginSelectRecordEventDetail,
    UISelectablePluginTarget,
} from './ui-selectable-plugin.type';

/**
 * @class
 * @memberof SharedComponents
 * @classdesc Class for ui-dynamic-table selectable plugin.
 * @alias UISelectablePlugin
 * @implements {ComponentPlugin}
 * @fires event:select-record
 * @param {UIDynamicTable} target Target instance.
 * @property {UIDynamicTable} target Target instance.
 */
export class UISelectablePlugin implements ComponentPlugin {
    private readonly target: UISelectablePluginTarget;

    constructor(target: UISelectablePluginTarget) {
        this.target = target;
    }

    /**
     * Resets array of selected records
     */
    initRowSelection(): void {
        this.target.selectedRecords = [];
    }

    /**
     * Selects the primary checkbox in header
     * @returns {HTMLInputElement | null}
     */
    getPrimaryControl(): NullOr<HTMLInputElement> {
        return this.target.querySelector<HTMLInputElement>(
            'input[name=toggle-all-page' + this.resolveKey() + ']'
        );
    }

    /**
     * Updates the "toggle all in a page" checkbox
     * @param {boolean} active
     */
    updateToggleAllControl(active: boolean): void {
        const control = this.getPrimaryControl();
        if (control) {
            control.checked = active;
        }
    }

    /**
     * Updates checkbox states for every row
     */
    updateSelectedRows(): void {
        const selectedRows = this.target.selectedRecords;
        const selectableCheckboxes = this.target.rowCheckboxes;
        const control = this.getPrimaryControl();
        this.target.disabledCheckboxes.forEach(
            (checkbox) => (checkbox.checked = false)
        );

        if (selectableCheckboxes.length === 0) {
            this.updateToggleAllControl(false);
            if (control) {
                control.disabled = true;
            }
            return;
        }
        if (control) {
            control.disabled = false;
        }
        let allChecked = true;
        selectableCheckboxes.forEach((checkbox) => {
            const result = selectedRows.includes(
                Number(checkbox.dataset.recordId)
            );
            if (!result) {
                allChecked = false;
            }
            checkbox.checked = result;
        });
        this.updateToggleAllControl(allChecked);
    }

    /**
     * Set checkbox state for every row in a current page
     * @param {boolean} value
     */
    toggleAllPage(value: boolean): void {
        this.target.rowCheckboxes.forEach((input) => (input.checked = value));
    }

    /**
     * Fires callback when row checkbox is toggled
     * @param {Event} event
     * @fires event:select-record
     * @private
     */
    private handleChange = (event: Event): void => {
        if (!(event.target instanceof HTMLInputElement)) {
            return;
        }
        if (event.target.dataset.event !== 'select-record') {
            return;
        }
        const checkboxes = this.target.activeRowCheckboxes;
        this.updateToggleAllControl(
            checkboxes.length === this.target.rowCheckboxes.length
        );
        this.target.dispatchCustomEvent('select-record', {
            records: [
                {
                    id: event.target.dataset.recordId!,
                    selected: event.target.checked,
                },
            ],
        } satisfies UISelectablePluginSelectRecordEventDetail);
    };

    /**
     * Fires callback when primary checkbox is toggled
     * @param {Event} event
     * @fires event:select-record
     * @private
     */
    private handleToggleAllPage = (event: Event): void => {
        if (!(event.target instanceof HTMLInputElement)) {
            return;
        }
        this.toggleAllPage(event.target.checked);
        this.target.dispatchCustomEvent('select-record', {
            records: Array.from(this.target.rowCheckboxes).map((el) => ({
                id: el.dataset.recordId!,
                selected: el.checked,
            })),
        } satisfies UISelectablePluginSelectRecordEventDetail);
    };

    /**
     * Modify table by inserting a selection checkbox to the first column
     * @inheritDoc
     */
    modifyRow(
        rowElement: HTMLTableRowElement,
        record: UIDynamicTableRecord
    ): HTMLTableRowElement {
        const key = this.target.getUniqueKeyOf(record);
        const selectable = this.target.isSelectable
            ? this.target.isSelectable(record)
            : true;
        rowElement.insertAdjacentElement(
            'afterbegin',
            this.target.createElement({
                tagName: 'td',
                classList: {
                    'col': true,
                    '-checkbox': true,
                },
                children: [
                    {
                        tagName: 'input',
                        attributes: {
                            'id':
                                'select-record-' +
                                String(key) +
                                this.resolveKey(),
                            'type': 'checkbox',
                            'disabled': !selectable ? 'disabled' : null,
                            'name':
                                'select-record-' +
                                String(key) +
                                this.resolveKey(),
                            'data-record-id': String(key),
                            'data-event': 'select-record',
                            'checked': this.target.selectedRecords.includes(
                                String(key)
                            )
                                ? ''
                                : null,
                        },
                    },
                    {
                        tagName: 'label',
                        attributes: {
                            for:
                                'select-record-' +
                                String(key) +
                                this.resolveKey(),
                        },
                    },
                ],
            })
        );
        return rowElement;
    }

    /**
     * Modify table by inserting a primary selection checkbox to the first column in header
     * @inheritDoc
     */
    modifyTable(uiTable: UITable): UITable {
        const targetTr = uiTable.querySelector('thead tr');
        if (targetTr) {
            targetTr.insertAdjacentElement(
                'afterbegin',
                this.target.createElement({
                    tagName: 'th',
                    attributes: {
                        class: 'col -checkbox -mobile-highlight',
                    },
                    children: [
                        {
                            tagName: 'input',
                            attributes: {
                                id: 'toggle-all-page' + this.resolveKey(),
                                name: 'toggle-all-page' + this.resolveKey(),
                                type: 'checkbox',
                            },
                        },
                        {
                            tagName: 'label',
                            attributes: {
                                for: 'toggle-all-page' + this.resolveKey(),
                            },
                        },
                    ],
                })
            );
        }
        return uiTable;
    }

    /**
     * @returns {string}
     */
    resolveKey(): string {
        const customKey = this.target.dataset.selectableKey;
        if (customKey) {
            return '-' + customKey;
        }
        return '';
    }

    /**
     * @inheritDoc
     */
    hydrate(): void {
        this.initRowSelection();
        const toggleAllCheckbox = this.target.querySelector<HTMLInputElement>(
            'input[name=toggle-all-page' + this.resolveKey() + ']'
        );
        if (toggleAllCheckbox) {
            toggleAllCheckbox.addEventListener(
                'change',
                this.handleToggleAllPage.bind(this)
            );
        }
        this.target
            .querySelector('tbody')
            ?.addEventListener('change', this.handleChange.bind(this));
    }
}

/**
 * @memberof UISelectablePlugin
 * @event select-record
 * @alias event:select-record
 * @type {object}
 * @property {Array<{id: string, selected: boolean}>} records - Array of selected records
 * @augments {CustomEvent}
 */
